import React, {
    FormEvent,
    ReactElement,
    useEffect, useMemo, useState,
} from "react";

import { createBrowserHistory } from "history";
import { MdCloudDone } from "react-icons/md";
import SavePrompt from "../save-prompt/SavePrompt";
import usePortal from "../../contexts/PortalContext";
import TimedMessage from "../timed-message/TimedMessage";
import {
    DeepComparer, isDeepEqual, isHtmlEqual, isEditorTextEqual,
} from "../../api";

export const BrowserHistory = createBrowserHistory();

type OnSaved = () => void;

export type SaveHandler = (event: FormEvent, callback: OnSaved) => void;
interface Props {
    errorMessage?: string;
    onSave?: SaveHandler;
    onReset?: () => void;
    saveText?: string;
    finishedSavingMessage?: string;
    shouldBlockNavigate?: boolean
}

export interface DirtyTracker {
    dirty(): void;
    clean(): void;
    useWatchProperty<T>(current: T, original: T): void;
    useWatchDeepCompareProperty<T>(current: T, original: T, comparer?: DeepComparer): void;
    get isDirty(): boolean;
    get saveComponent(): ReactElement | null;
    onSave(handler: SaveHandler): void;
    onReset(handler: () => void): void;
    useLoad(handler: () => void, dependenies: unknown[]): void;

    isEditorTextEqual(left: unknown, right: unknown): boolean;
    isHtmlEqual(left: string | undefined, right: string | undefined): boolean;
}

interface State {
    count: number;
    saver?: SaveHandler;
    reset?:() => void;
    values: boolean[];
}

export default function useDirtyTracker({
    onSave,
    onReset,
    saveText,
    errorMessage = "Are you sure you want to leave this page? You may have unsaved changes.",
    finishedSavingMessage,
    shouldBlockNavigate,
}: Props = {}): DirtyTracker {
    const states = useMemo<State>(() => ({
        count: 1,
        values: [],
    }), []);

    const [isShowSaved, setShowSaved] = useState<boolean>(false);

    const [isDirty, setIsDirty] = useState<boolean>(false);

    useEffect(() => {
        if (!shouldBlockNavigate) {
            return;
        }
        const isOverallDirty = isDirty;
        const beforeUnload = (evt: BeforeUnloadEvent): boolean => {
            if (isOverallDirty) {
                evt.preventDefault();
                // eslint-disable-next-line no-param-reassign
                evt.returnValue = "";
            }
            return !isOverallDirty;
        };

        let unblock: () => void;
        if (isOverallDirty) {
            unblock = BrowserHistory.block(transaction => {
                // eslint-disable-next-line no-alert
                if (window.confirm(errorMessage)) {
                    unblock();
                    transaction.retry();
                }
            });
        }
        window.addEventListener("beforeunload", beforeUnload);
        // eslint-disable-next-line consistent-return
        return () => {
            unblock?.();
            window.removeEventListener("beforeunload", beforeUnload);
        };
    }, [isDirty]);

    states.count = 1;
    if (onSave) {
        states.saver = onSave;
    }
    if (onReset) {
        states.reset = onReset;
    }

    const setDirty = (id: number) => {
        states.values[id] = true;
        setIsDirty(states.values.some(row => row));
    };

    const setClean = (id: number) => {
        states.values[id] = false;
        setIsDirty(states.values.some(row => row));
    };

    const reset = () => {
        states.values = [];
        setIsDirty(false);
    };

    return {
        dirty() {
            setDirty(0);
        },
        clean() {
            reset();
        },
        useWatchProperty<T>(current: T, original: T) {
            const index = states.count;
            states.count += 1;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                if (current !== original) {
                    setDirty(index);
                } else {
                    setClean(index);
                }
            }, [current]);
        },
        useWatchDeepCompareProperty<T>(current: T, original: T, comparer: DeepComparer) {
            const index = states.count;
            states.count += 1;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                if (!isDeepEqual(current, original, [], comparer)) {
                    setDirty(index);
                } else {
                    setClean(index);
                }
            }, [current]);
        },

        isHtmlEqual(left: string | undefined, right: string | undefined): boolean {
            return isHtmlEqual(left, right);
        },
        isEditorTextEqual(left: unknown, right: unknown): boolean {
            return isEditorTextEqual(left, right);
        },

        get isDirty() {
            return isDirty;
        },
        get saveComponent() {
            return !states.saver ? null : (
                <>
                    {finishedSavingMessage && (
                        <TimedMessage
                            shouldShow={isShowSaved}
                            alertClassName="text-white bg-default text-center"
                            titleClassName="text-white font-bolder"
                            title={(
                                <>
                                    <MdCloudDone />
                                    {" "}
                                    Saved
                                </>
                            )}
                            onClose={() => setShowSaved(false)}
                        >
                            {finishedSavingMessage}
                        </TimedMessage>
                    )}
                    <SavePrompt
                        onSave={evt => {
                            states.saver?.(evt, () => {
                                reset();
                                if (finishedSavingMessage) {
                                    setShowSaved(true);
                                }
                            });
                        }}
                        onReset={() => {
                            reset();
                            states.reset?.();
                        }}
                        text={saveText}
                        shouldShow={isDirty}
                    />
                </>

            );
        },
        onSave(handler: SaveHandler) {
            states.saver = handler;
        },
        onReset(handler: () => void) {
            states.reset = handler;
        },
        useLoad(handler: () => void, dependenies: unknown[]) {
            useEffect(() => {
                states.reset = handler;
                handler();
            }, dependenies);
        },

    };
}
